import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { useAuth } from "@/contexts/AuthContext";
import { API } from "@/lib/api";
import { toast } from "sonner";
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip,
  ResponsiveContainer, PieChart, Pie, Cell,
} from "recharts";
import {
  ChartPie, TrendUp, Users, Trophy, X as XIcon, Target, Megaphone,
  CaretLeft, CaretRight, CalendarBlank, FunnelSimple,
} from "@phosphor-icons/react";

const fmt = (n) => n?.toLocaleString("es-MX") || "0";
const MONTH_NAMES = ["Ene", "Feb", "Mar", "Abr", "May", "Jun", "Jul", "Ago", "Sep", "Oct", "Nov", "Dic"];
const PIE_COLORS = ["#3B82F6", "#10B981", "#F59E0B", "#EF4444", "#8B5CF6", "#EC4899", "#6366F1", "#F97316"];

function KpiCard({ icon: Icon, label, value, sub, color }) {
  const colors = {
    blue: "from-blue-500 to-blue-600", green: "from-emerald-500 to-emerald-600",
    red: "from-red-500 to-red-600", amber: "from-amber-500 to-amber-600",
    purple: "from-purple-500 to-purple-600", teal: "from-teal-500 to-teal-600",
  };
  return (
    <div className={`bg-gradient-to-br ${colors[color] || colors.blue} rounded-xl p-4 text-white shadow-lg`}>
      <div className="flex items-center gap-2 mb-2 opacity-90">
        <Icon size={18} weight="duotone" />
        <span className="text-xs font-medium uppercase tracking-wide">{label}</span>
      </div>
      <p className="text-2xl font-bold">{value}</p>
      {sub && <p className="text-xs opacity-75 mt-1">{sub}</p>}
    </div>
  );
}

export function MarketingDashboard({ onBack }) {
  const { token } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [period, setPeriod] = useState("all");
  const [year, setYear] = useState(new Date().getFullYear());

  const headers = { Authorization: `Bearer ${token}` };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      let url = `${API}/marketing/dashboard?`;
      if (period === "monthly") {
        const m = new Date().getMonth() + 1;
        const last = new Date(year, m, 0).getDate();
        url += `start_date=${year}-${String(m).padStart(2,"0")}-01&end_date=${year}-${String(m).padStart(2,"0")}-${last}`;
      } else {
        url += `start_date=${year}-01-01&end_date=${year}-12-31`;
      }
      const { data: d } = await axios.get(url, { headers });
      setData(d);
    } catch { toast.error("Error cargando dashboard"); }
    setLoading(false);
  }, [token, year, period]);

  useEffect(() => { load(); }, [load]);

  if (loading) return <div className="flex justify-center py-20"><div className="animate-spin rounded-full h-10 w-10 border-b-2 border-blue-600" /></div>;
  if (!data) return <div className="text-center py-16 text-slate-500"><ChartPie size={48} className="mx-auto mb-3 text-slate-300" /><p>Sin datos</p></div>;

  const statusPie = Object.entries(data.by_status || {}).map(([k, v]) => ({
    name: data.status_labels?.[k] || k, value: v, fill: data.status_colors?.[k] || "#94a3b8",
  }));
  const campaignPie = Object.entries(data.by_campaign || {}).map(([k, v]) => ({
    name: data.campaign_labels?.[k] || k, value: v, fill: data.campaign_colors?.[k] || PIE_COLORS[0],
  }));
  const monthData = Object.entries(data.by_month || {}).sort().map(([m, v]) => {
    const [, mo] = m.split("-");
    return { name: MONTH_NAMES[parseInt(mo) - 1] || m, total: v.total, ganados: v.ganados };
  });

  return (
    <div className="space-y-5" data-testid="marketing-dashboard">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <button onClick={onBack} className="p-2 hover:bg-slate-100 rounded-lg" data-testid="mkt-dash-back"><CaretLeft size={20} className="text-slate-500" /></button>
          <div>
            <h2 className="text-xl font-bold text-slate-900">Dashboard Marketing</h2>
            <p className="text-sm text-slate-500">Metricas de leads y campanas</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <div className="flex gap-0.5 bg-slate-100 p-0.5 rounded-lg">
            {[{ id: "monthly", label: "Mes" }, { id: "all", label: "Ano" }].map(p => (
              <button key={p.id} onClick={() => setPeriod(p.id)}
                className={`px-3 py-1.5 text-xs font-medium rounded-md transition-colors ${period === p.id ? "bg-white text-slate-900 shadow-sm" : "text-slate-500"}`}>
                {p.label}
              </button>
            ))}
          </div>
          <button onClick={() => setYear(y => y - 1)} className="p-1.5 hover:bg-slate-100 rounded-lg"><CaretLeft size={16} /></button>
          <span className="text-lg font-bold text-slate-700 min-w-[50px] text-center">{year}</span>
          <button onClick={() => setYear(y => y + 1)} className="p-1.5 hover:bg-slate-100 rounded-lg"><CaretRight size={16} /></button>
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
        <KpiCard icon={Target} label="Total Leads" value={fmt(data.total)} color="blue" />
        <KpiCard icon={Trophy} label="Ganados" value={fmt(data.won)} sub={`${data.conversion_rate}% conversion`} color="green" />
        <KpiCard icon={XIcon} label="Perdidos" value={fmt(data.lost)} color="red" />
        <KpiCard icon={TrendUp} label="En Proceso" value={fmt(data.in_progress)} color="amber" />
        <KpiCard icon={Users} label="Aprobados" value={fmt(data.approved)} sub="por admin" color="purple" />
        <KpiCard icon={Megaphone} label="Campanas" value={Object.keys(data.by_campaign || {}).length} color="teal" />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <div className="bg-white rounded-xl border p-5 shadow-sm">
          <h3 className="text-sm font-semibold text-slate-700 mb-4">Leads por Estatus</h3>
          {statusPie.length === 0 ? <p className="text-center text-slate-400 py-12 text-sm">Sin datos</p> : (
            <div className="flex items-center">
              <ResponsiveContainer width="55%" height={220}>
                <PieChart><Pie data={statusPie} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={85} innerRadius={40} paddingAngle={2}>
                  {statusPie.map((e, i) => <Cell key={i} fill={e.fill} />)}
                </Pie><Tooltip /></PieChart>
              </ResponsiveContainer>
              <div className="flex-1 space-y-1.5 text-xs">
                {statusPie.map(s => (
                  <div key={s.name} className="flex items-center gap-2">
                    <span className="w-2.5 h-2.5 rounded-full shrink-0" style={{ backgroundColor: s.fill }} />
                    <span className="text-slate-600 truncate">{s.name}</span>
                    <span className="ml-auto font-semibold text-slate-800">{s.value}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
        <div className="bg-white rounded-xl border p-5 shadow-sm">
          <h3 className="text-sm font-semibold text-slate-700 mb-4">Leads por Campana</h3>
          {campaignPie.length === 0 ? <p className="text-center text-slate-400 py-12 text-sm">Sin datos</p> : (
            <div className="flex items-center">
              <ResponsiveContainer width="55%" height={220}>
                <PieChart><Pie data={campaignPie} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={85} innerRadius={40} paddingAngle={2}>
                  {campaignPie.map((e, i) => <Cell key={i} fill={e.fill} />)}
                </Pie><Tooltip /></PieChart>
              </ResponsiveContainer>
              <div className="flex-1 space-y-1.5 text-xs">
                {campaignPie.map((c, i) => (
                  <div key={c.name} className="flex items-center gap-2">
                    <span className="w-2.5 h-2.5 rounded-full shrink-0" style={{ backgroundColor: c.fill }} />
                    <span className="text-slate-600 truncate">{c.name}</span>
                    <span className="ml-auto font-semibold text-slate-800">{c.value}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>

      {monthData.length > 0 && (
        <div className="bg-white rounded-xl border p-5 shadow-sm">
          <h3 className="text-sm font-semibold text-slate-700 mb-4">Leads por Mes</h3>
          <ResponsiveContainer width="100%" height={260}>
            <BarChart data={monthData}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
              <XAxis dataKey="name" tick={{ fontSize: 11 }} />
              <YAxis allowDecimals={false} tick={{ fontSize: 11 }} />
              <Tooltip />
              <Bar dataKey="total" name="Total" fill="#3B82F6" radius={[4, 4, 0, 0]} />
              <Bar dataKey="ganados" name="Ganados" fill="#10B981" radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      )}

      <div className="bg-white rounded-xl border p-5 shadow-sm">
        <h3 className="text-sm font-semibold text-slate-700 mb-4">Rendimiento por Asesor</h3>
        {(data.by_seller || []).length === 0 ? <p className="text-center text-slate-400 py-8 text-sm">Sin datos</p> : (
          <div className="overflow-auto">
            <table className="w-full text-xs" data-testid="mkt-seller-table">
              <thead>
                <tr className="bg-slate-800 text-white">
                  <th className="text-left py-2.5 px-3 font-semibold">Asesor</th>
                  <th className="text-right py-2.5 px-3 font-semibold">Total</th>
                  <th className="text-right py-2.5 px-3 font-semibold">Ganados</th>
                  <th className="text-right py-2.5 px-3 font-semibold">Perdidos</th>
                  <th className="text-right py-2.5 px-3 font-semibold">En Proceso</th>
                  <th className="text-right py-2.5 px-3 font-semibold">Conversion</th>
                </tr>
              </thead>
              <tbody>
                {data.by_seller.map((s, i) => (
                  <tr key={i} className="border-t hover:bg-slate-50/70">
                    <td className="py-2 px-3 text-slate-700 font-medium">{s.seller}</td>
                    <td className="py-2 px-3 text-right">{s.total}</td>
                    <td className="py-2 px-3 text-right text-emerald-600 font-semibold">{s.ganados}</td>
                    <td className="py-2 px-3 text-right text-red-500">{s.perdidos}</td>
                    <td className="py-2 px-3 text-right text-amber-600">{s.en_proceso}</td>
                    <td className="py-2 px-3 text-right font-semibold">{s.total ? Math.round(s.ganados / s.total * 100) : 0}%</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
